import { filterAttributeOptionsByEngineResults } from "@components/configuratorV2/components/SelectionDialog/helpers";
import { getQuantitySelection, getSizeSelection } from "@providers/BasketProvider/helpers";
import { Label, LabelSize, Title, TitleSize, styled } from "@sourceful/design-system";
import {
  AttributeValueType,
  Currency,
  EngineResponse,
  ProductMain,
  SanityProductPreset,
} from "@sourceful/shared-types";
import { formatTotalPrice, formatUnitPrice } from "@sourceful/shared-utils/engine-utils";
import ProductDetailSelect from "./ProductDetailSelect";

const ProductQuantitySelectorWrapper = styled("div", {
  display: "grid",
  gridRowGap: "$spacing-fluid-block-s",
  marginBlock: "$spacing-fluid-block-l",
});

export interface ProductDetailsQuantityAndPriceProps {
  selectedPreset: SanityProductPreset;
  product: ProductMain;
  engineMetadata: EngineResponse;
  currency?: Currency;
  updatePresetQuantity: (value: AttributeValueType) => void;
  updatePresetSize: (value: AttributeValueType, isCustomSize: boolean) => void;
  selectSizeEnabled?: boolean;
}

const ProductDetailsQuantityAndPrice = ({
  selectedPreset,
  engineMetadata,
  currency = "GBP",
  updatePresetQuantity,
  updatePresetSize,
  selectSizeEnabled = false,
}: ProductDetailsQuantityAndPriceProps) => {
  const totalPriceRaw = engineMetadata?.results?.price_amount?.total_price_pence;
  const unitPriceRaw = engineMetadata?.results?.price_amount?.price_per_unit_pence;

  const qtySelection = getQuantitySelection(selectedPreset.attributes);
  const qtyAttribute = qtySelection?.attribute;
  const qtyValue = qtyAttribute?.options.find(
    option => option.value.id === (qtySelection?.value as AttributeValueType).id
  );

  const sizeSelection = getSizeSelection(selectedPreset.attributes);
  const sizeAttribute = sizeSelection?.attribute;
  let sizeValue = sizeAttribute?.options.find(
    option => option.value.id === (sizeSelection?.value as AttributeValueType).id
  );

  if (!totalPriceRaw) {
    console.error(
      `Missing total price for selected preset ${selectedPreset.id} (${selectedPreset.name})`
    );
    return null;
  }

  return (
    <ProductQuantitySelectorWrapper>
      {qtyAttribute && qtyValue && (
        <ProductDetailSelect
          elementId="quantity-select"
          elementName="quantity"
          labelText={`Select ${qtyAttribute.title.toLowerCase()}`}
          pricesByValueId={engineMetadata.one_choice_options[qtyAttribute.id]}
          attribute={{
            ...qtyAttribute,
            options: filterAttributeOptionsByEngineResults({
              activeAttributeOptions: qtyAttribute.options || [],
              attributeEngineData: engineMetadata.one_choice_options[qtyAttribute.id] || {},
              isCustomQuote: false,
            }),
          }}
          selectedValue={qtyValue}
          handleSelect={selection => {
            updatePresetQuantity(selection.value);
          }}
        />
      )}

      {selectSizeEnabled && sizeAttribute && sizeValue && (
        <ProductDetailSelect
          elementId="size-select"
          elementName="size"
          labelText={`Select ${sizeAttribute.title.toLowerCase()}`}
          pricesByValueId={engineMetadata.one_choice_options[sizeAttribute.id]}
          attribute={{
            ...sizeAttribute,
            options: [
              ...filterAttributeOptionsByEngineResults({
                activeAttributeOptions: sizeAttribute.options || [],
                attributeEngineData: engineMetadata.one_choice_options[sizeAttribute.id] || {},
                isCustomQuote: false,
              }),
            ],
          }}
          selectedValue={sizeValue}
          handleSelect={selection => {
            updatePresetSize(selection.value, selection.isCustom ?? false);
          }}
        />
      )}

      <div>
        <Title size={TitleSize.Medium}>
          {selectSizeEnabled ? "From " : ""}
          {formatTotalPrice(totalPriceRaw, currency)}
        </Title>

        {unitPriceRaw && (
          <Label
            size={LabelSize.Medium}
            css={{
              color: "$colour-brand-gtm-grey-dark",
              marginTop: "$spacing-fluid-block-xx-small",
            }}
          >
            {formatUnitPrice(unitPriceRaw, currency)} per unit (excl. VAT)
          </Label>
        )}
      </div>
    </ProductQuantitySelectorWrapper>
  );
};

export default ProductDetailsQuantityAndPrice;
